<template>
  <StaticModal title="Filters" variant-class-name="filters" @close="emitClose">
    <AutomatedEmailFiltersContent
      v-model="localFilters"
      :tabs="tabs"
      :is-external-company="authStore.isExternalCompany"
      :is-saving="isSaving"
      show-clear-tooltip
      @apply="handleApply"
      @clear="handleClear"
    />
  </StaticModal>
</template>

<script lang="ts" setup>
  // Types
  import type { TListingFilters } from '~/misc/listings/types';
  import type { TAuthStore } from '~/modules/auth/store/types';
  import type { ITab } from '~/misc/v-tabs/types';

  // Config
  import { DEFAULT_LISTINGS_FILTERS, TABS } from '~/misc/listings/filters/config';

  // Store
  import { useAuthStore } from '~/modules/auth/store';

  // Components
  import StaticModal from '~/misc/modal/StaticModal.vue';
  import AutomatedEmailFiltersContent from '~/modules/opportunities/automated-email/settings/filters/content/AutomatedEmailFiltersContent.vue';

  const props = defineProps({
    filters: {
      type: Object as PropType<TListingFilters>,
      required: true,
    },
    applyCallback: {
      type: Function as PropType<
        ((filters: TListingFilters) => Promise<boolean> | void) | undefined
      >,
      default: undefined,
    },
    initialTabIndex: {
      type: Number,
      default: 0,
    },
  });

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const authStore: TAuthStore = useAuthStore();

  const localFilters: Ref<TListingFilters> = ref<TListingFilters>({
    ...DEFAULT_LISTINGS_FILTERS,
    ...props.filters,
    // "Hide contacts that have moved home" is locked on for automated emails
    exclude_sold_since: 1,
  });

  const isSaving: Ref<boolean> = ref<boolean>(false);

  const setIsSaving = (value: boolean) => {
    isSaving.value = value;
  };

  const setLocalFilters = (value: TListingFilters): void => {
    localFilters.value = { ...value };
  };

  const tabs: ComputedRef<ITab[]> = computed<ITab[]>(() => {
    const tabsArray: ITab[] = TABS.map((tab: ITab, index: number) => ({
      ...tab,
      active: index === props.initialTabIndex,
    }));

    if (!authStore.isExternalCompany) {
      tabsArray.splice(1);
    }

    return tabsArray;
  });

  const emitClose = (): void => emit('close');

  const handleApply = async (): Promise<void> => {
    if (isSaving.value) return;

    setIsSaving(true);

    try {
      const result: Promise<boolean> | void = props.applyCallback?.(localFilters.value);
      // Keep the modal open when the save explicitly fails so the user can retry.
      const success: boolean | void = result instanceof Promise ? await result : result;

      if (success !== false) {
        emitClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = (): void => {
    setLocalFilters({
      ...DEFAULT_LISTINGS_FILTERS,
      type: localFilters.value.type,
      // Keep the locked "moved home" toggle enabled
      exclude_sold_since: 1,
    });
  };
</script>
